import React, { createContext, useContext, useEffect, useState } from 'react'
import { useAuth } from './AuthContext'
import { storageService, PromptData as StoragePromptData } from '@/services/storageService'
import { supabase } from '@/lib/supabase'

interface ContextData {
  questions: string[]
  user_input: string
  context_output: string
}

interface RoleData {
  generated_roles: string[]
  selected_role: string
}

// Use the PromptData type from storageService
type PromptData = StoragePromptData

interface PromptContextType {
  promptData: PromptData | null
  loading: boolean
  error: string | null
  updatePrompt: (updates: Partial<PromptData>) => Promise<void>
  loadPrompt: (promptId: string) => Promise<void>
  refreshPrompt: () => Promise<void>
}

const PromptContext = createContext<PromptContextType | undefined>(undefined)

export const usePrompt = () => {
  const context = useContext(PromptContext)
  if (context === undefined) {
    throw new Error('usePrompt must be used within a PromptProvider')
  }
  return context
}

interface PromptProviderProps {
  children: React.ReactNode
  promptId?: string
}

export const PromptProvider: React.FC<PromptProviderProps> = ({ children, promptId }) => {
  const [promptData, setPromptData] = useState<PromptData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { user, isGuest } = useAuth()

  const loadPrompt = async (id: string) => {
    console.log('Loading prompt with ID:', id)
    setLoading(true)
    setError(null)

    try {
      const data = await storageService.loadPrompt(id, user)
      if (data) {
      console.log('Prompt loaded successfully:', data)
      setPromptData(data)
      } else {
        setError('Prompt not found')
      }
    } catch (err) {
      console.error('Error loading prompt:', err)
      setError('Failed to load prompt data')
    } finally {
      setLoading(false)
    }
  }

  const refreshPrompt = async () => {
    if (!promptData?.id) return

    console.log('Refreshing prompt data...')
    setLoading(true)
    setError(null)

    try {
      const data = await storageService.loadPrompt(promptData.id, user)
      if (data) {
      console.log('Prompt refreshed successfully:', data)
      setPromptData(data)
      } else {
        setError('Prompt not found')
      }
    } catch (err) {
      console.error('Error refreshing prompt:', err)
      setError('Failed to refresh prompt data')
    } finally {
      setLoading(false)
    }
  }

  const updatePrompt = async (updates: Partial<PromptData>) => {
    if (!promptData) return

    setError(null)

    try {
      await storageService.updatePrompt(promptData.id, updates, user)
      // Real-time subscription will handle the update for both authenticated and guest users
    } catch (err) {
      console.error('Error updating prompt:', err)
      setError('Failed to update prompt')
    }
  }

  useEffect(() => {
    console.log('PromptContext: promptId changed to:', promptId)
    if (promptId) {
      // Reset state when loading a new prompt
      console.log('PromptContext: Resetting state and loading new prompt')
      setPromptData(null)
      setError(null)
      loadPrompt(promptId)
    }
  }, [promptId, user, isGuest])

  // Set up real-time subscription for prompt updates (works for both authenticated and guest users)
  useEffect(() => {
    if (!promptId) return

    // Set up real-time for both authenticated and guest users (both use database now)
    const channel = supabase
      .channel(`prompt-${promptId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'prompts',
          filter: `id=eq.${promptId}`,
        },
        (payload: any) => {
          console.log('Real-time update received:', payload)
          // Update all fields from the database
          setPromptData(payload.new as PromptData)
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [promptId])

  const value = {
    promptData,
    loading,
    error,
    updatePrompt,
    loadPrompt,
    refreshPrompt
  }

  return (
    <PromptContext.Provider value={value}>
      {children}
    </PromptContext.Provider>
  )
}
